/**
 * namespace 를 확인후 해당 네임스페이스가 없을 경우 만들어서 리턴
 * @param  {} _namespace='window.console'
 * @param  {} _parent=window
 * @returns {Object}
 * @example
 * namespace('nc.lineage2.sampleApp');
 * var testApp = new nc.lineage2.sampleApp();
 */
export const namespace = (_namespace = 'window.console', _parent = window) => {
    if (typeof _namespace === 'string' && _namespace) {
        let parts = _namespace.split('.');
        for (let i = 0, max = parts.length; i < max; i++) {
            if (!_parent[parts[i]]) _parent[parts[i]] = {};
            _parent = _parent[parts[i]];
        }
    }
    return _parent;
};

/**
 * 객체가 유효한지 확인
 * @param  {Object} obj={} 탐색 대상 오브젝트
 * @param  {Array} keys=[] 키네임
 * @returns {Boolean}
 * @example
 * window.Twitch.Player 객체가 유효한지 확인하는 샘플
 * objHasKeys( window, ['Twitch', 'Player'] )
 */
export const objHasKeys = (obj = {}, keys = []) => {
    let next = keys.shift();
    return !!obj[next] && (!keys.length || objHasKeys(obj[next], keys));
};

/**
 * 쿠키 세팅
 * @param  {String} name 쿠키이름
 * @param  {String} value 쿠키값
 * @param  {Number} expiredays 만료날짜
 * @example
 * setCookie('topBanner', 'show', 1);
 */
export const setCookie = (name = '', value = '', expiredays = 1, domain = 'plaync.com') => {
    let todayDate = new Date();
    todayDate.setDate(todayDate.getDate() + expiredays);
    document.cookie = `${name}=${escape(value)}; path=/; domain=${domain};expires=${todayDate.toGMTString()};SameSite=Lax;`;
};

/**
 * 쿠키 확인
 * @param  {String} name 쿠키이름
 * @returns {String}
 * @example
 * getCookie('topBanner') == 'show' // true : 쿠키가 입력 되어 있는 상태
 */
export const getCookie = (name = '') => {
    let cookies = document.cookie;
    if (cookies.indexOf(name) == -1) return false;
    let cookie = cookies.substr(cookies.indexOf(name));
    cookie = cookie.split(';')[0];
    cookie = cookie.substr(cookie.indexOf('=') + 1);
    return cookie;
};

/**
 * 숫자 앞에 0 붙여주기 ex) '01', '001'
 * @param  {Number} n
 * @param  {Number} digits
 * @returns {String}
 * @example
 * leadingZeros( 3, 2 ) // return '03'
 */
export const leadingZeros = (n, digits) => {
    let zero = '';
    const len = n.toString().length;
    let i = 0;
    if (len < digits) {
        for (; i < digits - len; i += 1) {
            zero += '0';
        }
    }
    return zero + n;
};

/**
 * URL 파라미터 키네임으로 값 가져오기
 * @param  {String} name 키네임
 * @param  {String} url 필수아님
 * @returns {String}
 * @example
 * getParamByName('query'); // return '검색어'
 * getParamByName('query', 'https://lineage.plaync.com/search/index?query=test'); // return 'test'
 */
export const getParamByName = (name = '', url = window.location.href) => {
    name = name.replace(/[\[\]]/g, '\\$&');
    let regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
        results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

/**
 * URL에 파라미터 추가하기
 * @param  {String} _key
 * @param  {String} _val
 * @param  {String} _url
 * @returns {String}
 * @example
 * addParam( 'page_no', '43' ),
 */
export const addParam = (_key = 'key', _val = 0, _url = window.location.href) => {
    let re = new RegExp('([?&])' + _key + '=.*?(&|$)', 'i');
    let separator = _url.indexOf('?') !== -1 ? '&' : '?';
    if (_url.match(re)) {
        return _url.replace(re, '$1' + _key + '=' + _val + '$2');
    } else {
        return _url + separator + _key + '=' + _val;
    }
};

/**
 * 1000 단위 콤마
 * @example
 * withCommas( 1000000 ) => '1,000,000'
 */
export const withCommas = (_num) => {
    return _num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * 0~60초 : 지금 막
 * 1~60분	: n분 전
 * 1시간~24시간 : n시간 전
 * 1일이상 : 2011.05.23.
 *
 * 60000 = 60초
 * 3600000 = 60분
 * 86400000 = 24시간
 * @example
 * const now = +new Date("Sun Jul 19 2020 10:21:36");
 * const target = +new Date("Sun Jul 19 2020 10:21:30");
 * makeTime(target, now) // '지금 막'
 */
export const makeTime = (_time = +new Date(), _now = +new Date()) => {
    const diff = (_now - _time) / 1000;
    const past = new Date(_time);

    if (diff < 60) {
        _time = '지금 막';
    } else if (diff >= 60 && diff < 3600) {
        _time = parseInt(diff / 60) + '분 전';
    } else if (diff >= 3600 && diff < 86400) {
        _time = parseInt(diff / (60 * 60)) + '시간 전';
    } else if (diff >= 86400) {
        let MM = leadingZeros(past.getMonth() + 1, 2);
        let DD = leadingZeros(past.getDate(), 2);
        _time = `${past.getFullYear()}.${MM}.${DD}.`;
    }
    return _time;
};

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 * @example
 * isObject({a:1, b:2, c:{d:4}}) // true
 * isObject('nc.lineage2.sampleApp') // false
 */
export const isObject = (item) => {
    return item && typeof item === 'object' && !Array.isArray(item);
};

/**
 * Deep merge two objects.
 * @param target
 * @param ...sources
 * @example
 * mergeDeep({a:1}, {b:200}) // {a:1, b:200}
 */
export const mergeDeep = (target, ...sources) => {
    if (!sources.length) return target;
    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key])
                    Object.assign(target, {
                        [key]: {},
                    });
                mergeDeep(target[key], source[key]);
            } else {
                Object.assign(target, {
                    [key]: source[key],
                });
            }
        }
    }
    return mergeDeep(target, ...sources);
};

/**
 * hasClass
 * @example
 * const el = screen.getByTestId('hasClassTest');
 * hasClass(el, 'hasClass')
 */
export const hasClass = (ele, cls) => {
    return !!ele.className.match(new RegExp('(\\s|^)' + cls + '(\\s|$)'));
};

/**
 * addClass
 * @example
 */
export const addClass = (ele, cls) => {
    if (ele.classList) {
        ele.classList.add(cls);
    } else {
        if (!hasClass(ele, cls)) ele.className += ' ' + cls;
    }
};

/**
 * removeClass
 * @example
 */
export const removeClass = (ele, cls) => {
    if (ele.classList) {
        ele.classList.remove(cls);
    } else {
        if (hasClass(ele, cls)) {
            let reg = new RegExp('(\\s|^)' + cls + '(\\s|$)');
            ele.className = ele.className.replace(reg, ' ');
        }
    }
};

/**
 * toggleClass
 * @example
 * toggleClass(document.querySelector('p.special'), 'special');
 */
export const toggleClass = (el, className) => el.classList.toggle(className);

/**
 * getStyle
 * @example
 * getStyle(document.querySelector('p'), 'font-size'); // '16px'
 */
export const getStyle = (el, ruleName) => getComputedStyle(el)[ruleName];

/**
 * URL 유효성 검사
 * @example
 */
export const isLink = (_text) => {
    const pattern = /^(http[s]?:\/\/){0,1}(www\.){0,1}[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,5}[\.]{0,1}/;
    // const pattern = /(https?:\/\/)?w{0,3}\w*?\.(\w*?\.)?\w{2,3}\S*|www\.(\w*?\.)?\w*?\.\w{2,3}\S*|(\w*?\.)?\w*?\.\w{2,3}[\/\?]\S*/;
    return pattern.test(_text);
};

/**
 * 한글 조사 붙이기
 * @param  {String} _text=''
 * @param  {number} _josaStyle= 0:['를','을'] 1:['로','으로'] 2:['라고','이라고']
 * @example
 * josa('사과', 1);
 * @returns {String} '사과를'
 */
export const josa = (_text, _josaStyle) => {
    let code = _text.charCodeAt(_text.length - 1) - 44032;
    let josa = [
        ['를', '을'],
        ['로', '으로'],
        ['라고', '이라고'],
    ][_josaStyle];

    if ((code >= 0x3130 && code <= 0x318f) || (code >= 0xac00 && code <= 0xd7a3)) return _text + ''; // 한글이 아닐 경우

    if (code % 28 === 0) {
        return _text + josa[0]; // 받침 없음
    } else {
        return _text + josa[1]; // 받침 있음
    }
};

/**
 * html 태그 삭제
 * @example
 */
export const removeTag = (_html = '') => {
    return _html.replace(/<(\/)?([a-zA-Z0-9]*)(\s[a-zA-Z0-9]*=[^>]*)?(\s)*(\/)?>/gi, '');
};

/**
 * escapeTag html 태그 문자열로 변환
 * @example
 */
export const escapeTag = (_text = '') => {
    var htmlEscapes = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '/': '&#x2F;',
    };
    return ('' + _text).replace(/[&<>"'\/]/g, (match) => htmlEscapes[match]);
};

/**
 * isRequired 필수 파라미터
 * @example
 * let greetings = (name=isRequired(), message='Hello,') => {
 *     return `${message} ${name}`;
 * }
 */
export const isRequired = () => {
    throw new Error('This is a mandatory parameter.');
};

/**
 * scrollToTop
 * @example
 * scrollToTop();
 */
export const scrollToTop = () => {
    const c = document.documentElement.scrollTop || document.body.scrollTop;
    if (c > 0) {
        window.requestAnimationFrame(scrollToTop);
        window.scrollTo(0, c - c / 8);
    }
};

/**
 * show
 * @example
 * show(...document.querySelectorAll('img'));
 */
export const show = (...el) => [...el].forEach((e) => (e.style.display = ''));

/**
 * hide
 * @example
 * hide(document.querySelectorAll('img'));
 */
export const hide = (...el) => [...el].forEach((e) => (e.style.display = 'none'));

/**
 * insertAfter
 * @example
 * insertAfter(document.getElementById('myId'), '<p>after</p>');
 */
export const insertAfter = (el, htmlString) => el.insertAdjacentHTML('afterend', htmlString);

/**
 * insertBefore
 * @example
 * insertBefore(document.getElementById('myId'), '<p>before</p>'); // <p>before</p> <div id="myId">...</div>
 */
export const insertBefore = (el, htmlString) => el.insertAdjacentHTML('beforebegin', htmlString);

/**
 * @param {String} HTML representing a single element
 * @return {Element}
 * @example
 * var td = makeElement('<td>foo</td>'),
 * div = makeElement('<div><span>nested</span> <span>stuff</span></div>');
 */
export const makeElement = (html) => {
    let tmp = document.implementation.createHTMLDocument('');
    tmp.body.innerHTML = html.trim();
    return tmp.body.firstChild;
};

/**
 * makeElements
 * @param {String} HTML representing any number of sibling elements
 * @return {NodeList}
 * @example
 * var rows = makeElements('<tr><td>foo</td></tr><tr><td>bar</td></tr>');
 */
export const makeElements = (html) => {
    let tmp = document.implementation.createHTMLDocument('');
    tmp.body.innerHTML = html.trim();
    return tmp.body.childNodes;
};

/**
 * uniqueID generator
 * @param {String}
 * @return {}
 * @example
 *
 */
export const uniqueID = (_prefix = '_') => {
    return _prefix + Math.random().toString(36).substr(2, 9);
};

/**
 * removeElement
 * @param {String}
 * @return {}
 * @example
 *
 */
export const removeElement = (_el = {}) => {
    _el.parentNode.removeChild(_el);
};

/**
 * 두 숫자 가운데 랜덤 넘버 추출(실수)
 * @param {Number} min 최소
 * @param {Number} max 최대
 */
export const getRandomFloat = (min, max) => {
    return Math.round(min + Math.random() * (max - min));
};

/**
 * URL 복사
 * @param {string} _surl 경로
 */
export const copyUrl = async (_surl) => {
    try {
        await navigator.clipboard.writeText(_surl);
    } catch (err) {
        console.error('Failed to copy: ', err);
    }
};

/**
 * 공유하기 기능
 * @param {string} _surl 경로
 * @param {string} _title 타이틀
 */
export const shareUrl = async (_surl, _title) => {
    try {
        await navigator.share({ title: _title, url: _surl });
    } catch (err) {
        console.error('Failed to shareUrl: ', err);
    }
};
